import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { ApiError, NetworkError } from '@/lib/api-client';
import { fireGa4SignUp } from '@/lib/analytics/gtag';
import { buildMetaAttributionPayload } from '@/lib/analytics/meta-payload';
import { isStrongPassword } from '@/lib/password-rules';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';
import { PasswordRequirementsChecklist } from '@/components/auth/PasswordRequirementsChecklist';
import { TurnstileWidget } from '@/features/book/components/personen/TurnstileWidget';

/**
 * Probecover Stage 2 P5 — Light-Gate overlay reused for /figuren.
 *
 * Appears when a LOGGED-OUT visitor clicks "Figurenbild erstellen"
 * on /figuren. Registers (or logs in) the customer, forwards the
 * pre-checked Bestandskunden-Hinweis state (§ 7 Abs. 3 UWG) on the
 * register request, then signals success back to FigurenRoute,
 * which continues with photo upload + create-probe exactly as before.
 *
 * ═══ COPY DISCIPLINE (Sebastian ruling 2026-10-15 Option A + release) ═══
 *
 * Visible strings come from the shared book
 * `STRINGS.book.personen.lightgate*` catalog, byte-for-byte with
 * what the book LightGate renders. Two probe-specific keys exist
 * under `STRINGS.book.probe.lightGate`:
 *   - `registerCta` = "Figurenbild erstellen" — primary CTA on
 *     the register form. Replaces `lightgateRegisterCta`
 *     ("Weiter zur Geschichte") which read wrong in the probe
 *     funnel.
 *   - `description` — sr-only `DialogDescription` for both
 *     register and login modes. Replaces `lightgateSubtitle`
 *     which mentioned "Buch".
 * The Bestandskunden-Hinweis (§ 7 Abs. 3 UWG, 2026-10-31) uses the
 * shared `STRINGS.auth.marketingNotice.{checkboxLabel,hint}` strings
 * also rendered by the book LightGateOverlay + standalone SignupRoute.
 * Login primary CTA still uses `lightgateLoginCta` = "Anmelden"
 * (reads correctly in both flows).
 *
 * Discipline note: the book LightGate does NOT swap submit labels
 * during submission ("Wird erstellt …" etc.) — the `disabled`
 * state is the visual feedback. The probe overlay follows the
 * same pattern.
 *
 * SCOPE
 *   - Register: email + password + Turnstile + terms accepted +
 *     pre-checked Bestandskunden-Hinweis (§ 7 Abs. 3 UWG, 2026-10-31).
 *     signupSource='probecover' (implicit marker; mirrors
 *     'book_lightgate'). The checkbox state is forwarded on the
 *     register request as `marketingObjection = !checked`; the
 *     backend writes `marketing_notice_at = now()` always, and
 *     also sets `marketing_unsubscribed_at = now()` when
 *     marketingObjection === true. There is no follow-on
 *     POST /api/users/consent write.
 *   - Login (returning customer): email + password, no Turnstile
 *     (login is a different endpoint; backend doesn't gate login
 *     on Turnstile today). The register-only Bestandskunden-Hinweis
 *     is NOT shown in login mode — login is for already-registered
 *     accounts whose notice was captured at signup.
 *
 * The overlay NEVER navigates. FigurenRoute reads success via the
 * `onAuthenticated` callback and runs its existing handleSubmit.
 *
 * NON-GOALS (vs. the book LightGateOverlay):
 *   - No photo upload or create-order inside the overlay (the probe
 *     flow does these in FigurenRoute post-auth; cost is low enough
 *     that one navigation-free path is sufficient).
 *   - No TITELWUNSCH or Jugendschutz rejection flow (probe-free).
 */

// Sebastian ruling 2026-11-02 (mobile-audit sweep). Pre-fix:
// `top-8 translate-y-0` positioned the sheet 2rem from the top with
// no height cap, no overflow, no safe-area padding. Same symptom the
// book LightGateOverlay was fixed for on 2026-11-02 (customer report
// + PostHog Android Light-Gate completion 42% vs. iOS 80%) — on
// Android 360x400 (keyboard open) the "Figurenbild erstellen" button,
// the terms checkbox, and the mode-switch link sat below the fold
// with no inner scroll. Pattern mirrored verbatim from
// `LightGateOverlay.tsx:145-146 + :472-504`:
//   - `max-h-[var(--lg-vh,calc(100dvh-2rem))]` caps the sheet to the
//     dynamic viewport. `--lg-vh` is set from
//     `window.visualViewport.height` while the overlay is open so
//     iOS Safari (where dvh does NOT shrink under the keyboard)
//     honors the live viewport too. Shared variable name with the
//     book LightGate — the two overlays are never open at the same
//     time (customer is either in /book flow or /figuren flow).
//   - `overflow-y-auto overscroll-contain` makes the sheet
//     contents scrollable inside the sheet; the focused input is
//     auto-scrolled into view by the browser on keyboard open.
//   - `pb-[max(1.5rem,env(safe-area-inset-bottom))]` keeps the last
//     element clear of the iPhone home indicator.
const CONTENT_CLASS =
  'max-w-[480px] top-4 translate-y-0 max-h-[var(--lg-vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain border-0 bg-dream-white p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-xl';
const BUTTON_GOLD =
  'h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';
const FIELD_CLASS = 'builder-field h-11 w-full px-3 text-base text-dream-navy outline-none';
const ERROR_CLASS = 'text-sm text-error';

type Mode = 'register' | 'login';
type Phase =
  | { readonly kind: 'form' }
  | { readonly kind: 'submitting' }
  | { readonly kind: 'error'; readonly message: string };

export interface ProbeLightGateOverlayProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /**
   * Fires after register/login succeeds AND the marketing-consent
   * row has been written (if applicable). FigurenRoute reacts by
   * continuing its existing submit flow (upload → createProbe).
   */
  readonly onAuthenticated: () => void;
  /**
   * F12 (Sebastian field finding 2026-10-17) — initial overlay
   * mode. 'register' is the submit-the-form path for a brand-new
   * visitor; 'login' is the deep-link path for a returning
   * customer opening `?probe=<id>` from their probe_ready mail
   * while logged out. The customer can still toggle between
   * modes via the "schon ein Konto?" / "noch kein Konto?" link.
   * Defaults to 'register' to preserve pre-fix behaviour.
   */
  readonly initialMode?: Mode;
}

export function ProbeLightGateOverlay({
  open,
  onOpenChange,
  onAuthenticated,
  initialMode = 'register',
}: ProbeLightGateOverlayProps) {
  const register = useAuthStore((s) => s.register);
  const login = useAuthStore((s) => s.login);

  const [mode, setMode] = useState<Mode>(initialMode);
  const [phase, setPhase] = useState<Phase>({ kind: 'form' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Bestandskunden-Hinweis (§ 7 Abs. 3 UWG). Pre-checked opt-out box.
  // Sent to backend as `marketingObjection = !marketingAccepted`.
  // Supersedes the pre-2026-10-31 non-pre-checked consent checkbox +
  // the follow-on POST /api/users/consent write (both removed).
  const [marketingAccepted, setMarketingAccepted] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Double-submit guard (2026-10-30). The `phase.kind === 'submitting'`
  // signal only flips on the first setState INSIDE the handler, so
  // a rapid double tap or submit + Enter within one tick can land
  // two POSTs before the next render disables the button. The ref
  // gates the handler synchronously; the mirrored state drives the
  // button disable for the UI.
  const authSubmittingRef = useRef(false);
  const [authSubmitting, setAuthSubmitting] = useState(false);

  // Reset the local form state every time the overlay closes so a
  // second open doesn't carry over a stale half-filled attempt.
  // F12 (2026-10-17): reset to `initialMode` (not hardcoded
  // 'register') so a probe-deep-link re-open stays in login mode
  // if the parent still wants it.
  useEffect(() => {
    if (!open) {
      setMode(initialMode);
      setPhase({ kind: 'form' });
      setEmail('');
      setPassword('');
      setTermsAccepted(false);
      setMarketingAccepted(true);
      setTurnstileToken(null);
    }
  }, [open, initialMode]);

  // F12 — the mode state was captured at mount via
  // `useState<Mode>(initialMode)`. When the parent flips
  // initialMode AND open together (deep-link bootstrap: logged-
  // out visitor with `?probe=<id>` opens overlay in login mode),
  // the mode state would otherwise stay on the mount-time
  // default ('register'). This effect syncs the mode state to
  // the parent's intent whenever initialMode changes while
  // the overlay is open.
  useEffect(() => {
    if (open) {
      setMode(initialMode);
    }
  }, [initialMode, open]);

  // Sebastian ruling 2026-11-02 (mobile-audit sweep). While the
  // overlay is open, mirror `window.visualViewport.height` into the
  // `--lg-vh` CSS variable so the sheet's max-height tracks the live
  // visible area under an open mobile keyboard — iOS Safari dvh does
  // NOT shrink with the keyboard, visualViewport does. Shared
  // variable name with the book LightGateOverlay effect; the two
  // overlays are never open at the same time. Also tags the body
  // with `data-light-gate-open` so the Cookiebot renew-widget hide
  // CSS rule covers this surface too.
  useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    document.body.setAttribute('data-light-gate-open', 'true');
    const vv = window.visualViewport;
    if (vv === null || vv === undefined) {
      return () => {
        document.body.removeAttribute('data-light-gate-open');
      };
    }
    // Reserve 2rem (32px) for top-4 offset + bottom breathing room.
    const sync = () => {
      document.documentElement.style.setProperty('--lg-vh', `${Math.round(vv.height - 32)}px`);
    };
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      document.documentElement.style.removeProperty('--lg-vh');
      document.body.removeAttribute('data-light-gate-open');
    };
  }, [open]);

  const handleRegisterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Double-submit guard (2026-10-30) — synchronous ref read so a
    // rapid double tap or submit + Enter within one tick cannot
    // reach `register()` twice before the first POST resolves.
    if (authSubmittingRef.current) return;
    authSubmittingRef.current = true;
    setAuthSubmitting(true);
    setPhase({ kind: 'submitting' });
    try {
      await register({
        email,
        password,
        acceptedTerms: true,
        signupSource: 'probecover',
        turnstileToken: turnstileToken ?? '',
        meta: buildMetaAttributionPayload(),
        marketingObjection: !marketingAccepted,
      });
      // GA4 funnel — sign_up with method='figurenbild' (Sebastian
      // ruling 2026-10-21). Fires after a successful register from
      // the probe light-gate; the Figurenbild feature itself sits
      // behind FEATURE_PROBECOVER_ENABLED, so this event is dormant
      // until the flag flips on.
      fireGa4SignUp({ method: 'figurenbild' });
      onAuthenticated();
    } catch (err) {
      // Never surface err.envelope.user_message directly — map
      // backend status to the German PWA STRINGS catalog. The 409
      // path reuses SignupRoute's `duplicateEmail` string; a
      // returning customer who hits this can switch to login via
      // the "schon ein Konto?" link below (mode-switch preserves
      // the email + password state).
      let message: string;
      if (err instanceof NetworkError) {
        message = STRINGS.auth.errors.networkError;
      } else if (err instanceof ApiError) {
        if (err.httpStatus === 409) {
          message = STRINGS.auth.errors.duplicateEmail;
        } else if (err.httpStatus === 401) {
          message = STRINGS.auth.errors.invalidCredentials;
        } else {
          message = STRINGS.auth.errors.genericBackend;
        }
      } else {
        message = STRINGS.auth.errors.genericBackend;
      }
      setPhase({ kind: 'error', message });
    } finally {
      authSubmittingRef.current = false;
      setAuthSubmitting(false);
    }
  };

  const handleLoginSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Double-submit guard (2026-10-30) — same shape as
    // handleRegisterSubmit above.
    if (authSubmittingRef.current) return;
    authSubmittingRef.current = true;
    setAuthSubmitting(true);
    setPhase({ kind: 'submitting' });
    try {
      await login({ email, password });
      // Marketing-consent is REGISTER-ONLY (Sebastian ruling
      // 2026-10-17). A returning customer logging in through
      // the probe funnel is NOT presented with the marketing
      // checkbox — the opt-in surface lives on the register
      // form and on the main /figuren form (via
      // `showConsentCheckbox` when `user.consentMarketing ===
      // false`). The book flow has its own marketing-consent
      // surface for returning customers who skip the probe.
      onAuthenticated();
    } catch (err) {
      // Never surface err.envelope.user_message directly — map
      // backend status / error_code to the German PWA STRINGS
      // catalog. 429 keeps the login-throttle copy; prelaunch_locked
      // maps to the verbatim-locked PWA entry (same text as the
      // backend catalog, so no behavior change for that gate).
      let message: string;
      if (err instanceof NetworkError) {
        message = STRINGS.auth.errors.networkError;
      } else if (err instanceof ApiError) {
        if (err.httpStatus === 429) {
          message = STRINGS.auth.errors.loginThrottled;
        } else if (err.errorCode === 'prelaunch_locked') {
          message = STRINGS.auth.errors.prelaunchLocked;
        } else if (err.httpStatus === 401) {
          message = STRINGS.auth.errors.invalidCredentials;
        } else {
          message = STRINGS.auth.errors.genericBackend;
        }
      } else {
        message = STRINGS.auth.errors.genericBackend;
      }
      setPhase({ kind: 'error', message });
    } finally {
      authSubmittingRef.current = false;
      setAuthSubmitting(false);
    }
  };

  const registerReady =
    email.trim().length > 0 &&
    isStrongPassword(password) &&
    termsAccepted &&
    turnstileToken !== null &&
    phase.kind !== 'submitting' &&
    !authSubmitting;
  const loginReady =
    email.trim().length > 0 &&
    password.length > 0 &&
    phase.kind !== 'submitting' &&
    !authSubmitting;

  // Visible strings come from the shared book
  // `STRINGS.book.personen.lightgate*` catalog (Sebastian Option A
  // 2026-10-15). Two labels were released probe-specific in a
  // follow-up because the book copy read wrong in the probe
  // context:
  //   - register primary CTA → `book.probe.lightGate.registerCta`
  //     = "Figurenbild erstellen" (replaces
  //     `lightgateRegisterCta` = "Weiter zur Geschichte")
  //   - sr-only DialogDescription → `book.probe.lightGate
  //     .description` = "Mit deinem Konto erstellen wir dein
  //     Figurenbild und schicken es dir per E-Mail." (replaces
  //     `lightgateSubtitle` which mentioned "Buch").
  // Login primary CTA still uses the book-catalog
  // `lightgateLoginCta` = "Anmelden" (reads correctly in both
  // flows).
  const copy = STRINGS.book.personen;
  const probeCopy = STRINGS.book.probe.lightGate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={CONTENT_CLASS}>
        <DialogTitle className="text-xl font-bold text-dream-navy">
          {mode === 'register' ? copy.lightgateTitle : copy.lightgateLoginHeading}
        </DialogTitle>
        <DialogDescription className="sr-only">{probeCopy.description}</DialogDescription>

        {mode === 'register' ? (
          <form onSubmit={(e) => void handleRegisterSubmit(e)} className="mt-4 flex flex-col gap-3">
            <input
              type="email"
              autoComplete="email"
              placeholder={copy.lightgateEmailLabel}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={FIELD_CLASS}
              required
            />
            <input
              type="password"
              autoComplete="new-password"
              placeholder={copy.lightgatePasswordLabel}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={FIELD_CLASS}
              required
            />
            {/* F16 (Sebastian field finding 2026-10-17) — the
                probe overlay renders on a `bg-dream-white` dialog
                content (CONTENT_CLASS above). `tone="light"` +
                `pendingTone="error"` are the same props the book
                LightGateOverlay passes
                (LightGateOverlay.tsx:609) — reusing them keeps the
                two surfaces visually identical. Default `tone="dark"`
                was invisible against the white backdrop (text-dream-
                white/70 on bg-dream-white). */}
            <PasswordRequirementsChecklist password={password} tone="light" pendingTone="error" />

            <label className="flex items-start gap-2 text-sm text-dream-navy">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1"
              />
              <span>{copy.lightgateTermsCheckbox}</span>
            </label>

            {/* Bestandskunden-Hinweis (§ 7 Abs. 3 UWG). Pre-checked
                opt-out box + hint. Does not gate submit. */}
            <div className="space-y-1">
              <label className="flex items-start gap-2 text-sm text-dream-navy">
                <input
                  type="checkbox"
                  checked={marketingAccepted}
                  onChange={(e) => setMarketingAccepted(e.target.checked)}
                  className="mt-1"
                />
                <span>{STRINGS.auth.marketingNotice.checkboxLabel}</span>
              </label>
              <p className="pl-[22px] text-xs text-dream-navy/70 leading-relaxed">
                {STRINGS.auth.marketingNotice.hint}
              </p>
            </div>

            <TurnstileWidget onToken={setTurnstileToken} />

            {phase.kind === 'error' && (
              <p role="alert" className={ERROR_CLASS}>
                {phase.message}
              </p>
            )}

            <button type="submit" disabled={!registerReady} className={BUTTON_GOLD}>
              {probeCopy.registerCta}
            </button>

            <button
              type="button"
              onClick={() => {
                setMode('login');
                setPhase({ kind: 'form' });
              }}
              className="text-center text-sm text-dream-navy/70 underline"
            >
              {copy.lightgateHasAccountLink}
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => void handleLoginSubmit(e)} className="mt-4 flex flex-col gap-3">
            <input
              type="email"
              autoComplete="email"
              placeholder={copy.lightgateEmailLabel}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={FIELD_CLASS}
              required
            />
            <input
              type="password"
              autoComplete="current-password"
              placeholder={copy.lightgateLoginPasswordLabel}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={FIELD_CLASS}
              required
            />

            {phase.kind === 'error' && (
              <p role="alert" className={ERROR_CLASS}>
                {phase.message}
              </p>
            )}

            <button type="submit" disabled={!loginReady} className={BUTTON_GOLD}>
              {copy.lightgateLoginCta}
            </button>

            <button
              type="button"
              onClick={() => {
                setMode('register');
                setPhase({ kind: 'form' });
              }}
              className="text-center text-sm text-dream-navy/70 underline"
            >
              {copy.lightgateNoAccountLink}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
